import { useEffect, useRef, useState } from 'react'
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { api, session } from '../lib/api'
import { useApi } from '../lib/hooks'
import { initials } from '../lib/format'
import {
  IconBox, IconCash, IconChart, IconCog, IconExit, IconGrid, IconLeaf, IconRoute, IconScale,
  IconStore, IconTag, IconTruck, IconUsers,
} from '../ui/kit'
import { ControlRoom } from './ControlRoom'
import { Collections } from './Collections'
import { LotTrace } from './LotTrace'
import { Suppliers } from './Suppliers'
import { Pricing } from './Pricing'
import { Buyers } from './Buyers'
import { Orders } from './Orders'
import { Logistics } from './Logistics'
import { Finance } from './Finance'
import { Payouts } from './Payouts'
import { Reports } from './Reports'
import { Setup } from './Setup'
import { Offers } from './Offers'
import { activeNavFor, BreadcrumbProvider, Breadcrumbs } from './breadcrumbs'

interface NavItem {
  to: string
  label: string
  icon: JSX.Element
  roles?: string[]
  badgeKey?: 'exceptions'
}

const NAV: Array<{ group: string; items: NavItem[] }> = [
  {
    group: 'Today',
    items: [
      { to: '/console', label: 'Today', icon: <IconGrid size={17} />, badgeKey: 'exceptions' },
      { to: '/console/collections', label: 'Collections', icon: <IconScale size={17} /> },
      { to: '/console/logistics', label: 'Deliveries', icon: <IconTruck size={17} /> },
    ],
  },
  {
    group: 'Buying',
    items: [
      { to: '/console/suppliers', label: 'Farmers', icon: <IconUsers size={17} /> },
      { to: '/console/offers', label: 'Farmer offers', icon: <IconLeaf size={17} /> },
      { to: '/console/pricing', label: 'Prices', icon: <IconTag size={17} />, roles: ['admin', 'ops'] },
    ],
  },
  {
    group: 'Selling',
    items: [
      { to: '/console/buyers', label: 'Buyers', icon: <IconStore size={17} /> },
      { to: '/console/orders', label: 'Orders', icon: <IconRoute size={17} /> },
    ],
  },
  {
    group: 'Money',
    items: [
      { to: '/console/payouts', label: 'Paying farmers', icon: <IconCash size={17} /> },
      { to: '/console/finance', label: 'Invoices', icon: <IconStore size={17} />, roles: ['admin', 'finance'] },
      { to: '/console/reports', label: 'Profit per kilo', icon: <IconChart size={17} /> },
      { to: '/console/setup/expenses', label: 'Expenses', icon: <IconBox size={17} />, roles: ['admin', 'finance'] },
    ],
  },
  {
    group: 'Setup',
    items: [
      { to: '/console/setup', label: 'Settings & lists', icon: <IconCog size={17} /> },
    ],
  },
]

export function ConsoleShell({ onSignOut }: { onSignOut: () => void }) {
  const nav = useNavigate()
  const user = session.user
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('ledger.rail') === 'collapsed')
  const [theme, setTheme] = useState(() => localStorage.getItem('ledger.theme') ?? 'light')

  const { data: exceptions } = useApi<Array<{ severity: string }>>('/ops/exceptions')
  const openCritical = exceptions?.filter((e) => e.severity === 'critical').length ?? 0

  const { pathname } = useLocation()
  const activeNav = activeNavFor(pathname, NAV.flatMap((g) => g.items.map((i) => i.to)))

  // The page area is its own scroll container, so moving to a new page would otherwise
  // keep the last page's scroll position and open somewhere down the middle.
  const pageRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    pageRef.current?.scrollTo({ top: 0 })
  }, [pathname])

  useEffect(() => {
    document.documentElement.setAttribute('data-density', 'compact')
    if (theme === 'system') document.documentElement.removeAttribute('data-theme')
    else document.documentElement.setAttribute('data-theme', theme)
    localStorage.setItem('ledger.theme', theme)
  }, [theme])

  useEffect(() => {
    localStorage.setItem('ledger.rail', collapsed ? 'collapsed' : 'open')
  }, [collapsed])

  async function signOut() {
    if (!window.confirm('Sign out of Ledger?')) return
    await api.logout()
    onSignOut()
    nav('/login', { replace: true })
  }

  const roles = user?.roles ?? []
  const canSee = (item: NavItem) => !item.roles || item.roles.some((r) => roles.includes(r))

  return (
    <BreadcrumbProvider>
    <div className={`console ${collapsed ? 'rail-collapsed' : ''}`}>
      <nav className="rail">
        <div className="rail-brand">
          <span className="rail-mark"><IconLeaf size={16} color="#fff" /></span>
          {!collapsed && <span>Ledger</span>}
        </div>

        <div className="rail-nav">
          {NAV.map((group) => {
            const items = group.items.filter(canSee)
            if (items.length === 0) return null
            return (
              <div key={group.group}>
                {!collapsed && (
                  <div className="rail-group"><span>{group.group}</span></div>
                )}
                {items.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    // Not NavLink: its own matching lights every item whose path is a
                    // prefix of where you are, so a nested route lit two at once.
                    className={`rail-link ${activeNav === item.to ? 'active' : ''}`}
                    title={collapsed ? item.label : undefined}
                    aria-current={activeNav === item.to ? 'page' : undefined}
                  >
                    {item.icon}
                    {!collapsed && <span className="grow truncate">{item.label}</span>}
                    {!collapsed && item.badgeKey === 'exceptions' && openCritical > 0 && (
                      <span className="badge">{openCritical}</span>
                    )}
                  </Link>
                ))}
              </div>
            )
          })}
        </div>

        <div className="rail-foot">
          <span className="avatar">{initials(user?.fullName)}</span>
          {collapsed && (
            <button className="signout" onClick={signOut} title="Sign out" aria-label="Sign out"
              style={{ padding: 'var(--sp-4)' }}>
              <IconExit size={14} />
            </button>
          )}
          {!collapsed && (
            <>
              <div className="who grow truncate">
                <b className="truncate">{user?.fullName}</b>
                <span>{roles.join(' · ').replace(/_/g, ' ')}</span>
              </div>
              <button className="signout" onClick={signOut} aria-label="Sign out">
                <IconExit size={14} />
                Sign out
              </button>
            </>
          )}
        </div>
      </nav>

      <div className="console-main">
        <header className="topbar">
          <button
            className="icon-btn"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <Breadcrumbs />
          <div className="spacer" />
          <span className="tb-org">{user?.org.name}</span>
          <span className="tb-meta">
            {new Date().toLocaleDateString('en-KE', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
          </span>
          <button
            className="icon-btn"
            onClick={() => setTheme(theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light')}
            title={`Theme: ${theme} — click for ${theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light'}`}
            aria-label={`Theme: ${theme}. Click to change.`}
          >
            {theme === 'dark' ? (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M20 14.5A8.5 8.5 0 019.5 4a8.5 8.5 0 1010.5 10.5z" />
              </svg>
            ) : (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <circle cx="12" cy="12" r="4" />
                <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4" />
              </svg>
            )}
          </button>
        </header>

        <div className="page-scroll" ref={pageRef}>
        <Routes>
          <Route index element={<ControlRoom />} />
          <Route path="collections" element={<Collections />} />
          <Route path="collections/:code" element={<LotTrace />} />
          <Route path="suppliers" element={<Suppliers />} />
          <Route path="pricing" element={<Pricing />} />
          <Route path="buyers" element={<Buyers />} />
          <Route path="orders" element={<Orders />} />
          <Route path="logistics" element={<Logistics />} />
          <Route path="finance" element={<Finance />} />
          <Route path="payouts" element={<Payouts />} />
          <Route path="reports" element={<Reports />} />
          <Route path="offers" element={<Offers />} />
          <Route path="setup" element={<Setup />} />
          <Route path="setup/:resource" element={<Setup />} />
          <Route path="*" element={<Navigate to="/console" replace />} />
        </Routes>
        </div>
      </div>
    </div>
    </BreadcrumbProvider>
  )
}
